import React, { useState } from "react"

import { trpc } from "../../lib/trpc"

const CreatorManagement: React.FC = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [editingCreator, setEditingCreator] = useState<any>(null)

  const { data: creators, isLoading, refetch } = trpc.contentManagement.listCreators.useQuery()
  const addCreatorMutation = trpc.contentManagement.addCreator.useMutation()
  const updateCreatorMutation = trpc.contentManagement.updateCreatorConfig.useMutation()
  const triggerScrapingMutation = trpc.contentManagement.triggerScraping.useMutation()

  const handleAddCreator = async (creatorData: any) => {
    try {
      await addCreatorMutation.mutateAsync(creatorData)
      setIsAddModalOpen(false)
      void refetch()
    } catch (error) {
      console.error("Failed to add creator:", error)
    }
  }

  const handleUpdateCreator = async (creatorId: string, updates: any) => {
    try {
      await updateCreatorMutation.mutateAsync({ creatorId, ...updates })
      setEditingCreator(null)
      void refetch()
    } catch (error) {
      console.error("Failed to update creator:", error)
    }
  }

  const handleTriggerScraping = async (creatorId: string) => {
    try {
      await triggerScrapingMutation.mutateAsync({ creatorId })
      void refetch()
    } catch (error) {
      console.error("Failed to trigger scraping:", error)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold ">Creator Management</h2>
          <p className="">Manage creators to scrape content from</p>
        </div>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Add Creator
        </button>
      </div>

      {/* Creators Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {creators?.map((creator: any) => (
          <div key={creator.id} className="bg-background p-6 rounded-lg shadow hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-foregroundx rounded-full flex items-center justify-center">
                  <span className="text-lg">{creator.platform === "linkedin" ? "💼" : "🐦"}</span>
                </div>
                <div>
                  <h3 className="text-lg font-medium ">{creator.name}</h3>
                  <p className="text-sm text-gray-500">@{creator.handle}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button onClick={() => setEditingCreator(creator)} className="text-gray-400 hover:">
                  ⚙️
                </button>
                <button
                  onClick={() => handleTriggerScraping(creator.id)}
                  disabled={triggerScrapingMutation.isLoading}
                  className=" hover:text-blue-800 disabled:opacity-50"
                >
                  🔄
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Platform:</span>
                <span className="font-medium capitalize">{creator.platform}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Posts Scraped:</span>
                <span className="font-medium">{creator.scrapedCount || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Avg Engagement:</span>
                <span className="font-medium">{creator.avgEngagement || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Last Scraped:</span>
                <span className="font-medium">
                  {creator.lastScrapedAt ? new Date(creator.lastScrapedAt).toLocaleDateString() : "Never"}
                </span>
              </div>
            </div>

            {creator.description && <p className="text-sm  mt-3 line-clamp-2">{creator.description}</p>}

            <div className="mt-4 pt-4 border-t">
              <div className="flex space-x-2">
                <button
                  onClick={() => handleTriggerScraping(creator.id)}
                  disabled={triggerScrapingMutation.isLoading}
                  className="flex-1 bg-blue-100 text-blue-700 py-2 px-4 rounded hover:bg-blue-200 transition-colors disabled:opacity-50"
                >
                  {triggerScrapingMutation.isLoading ? "Scraping..." : "Scrape Now"}
                </button>
                <button
                  onClick={() => setEditingCreator(creator)}
                  className="bg-foregroundx text-primary py-2 px-4 rounded  transition-colors"
                >
                  Configure
                </button>
              </div>
            </div>
          </div>
        ))}

        {(!creators || creators.length === 0) && (
          <div className="col-span-full text-center py-12">
            <div className="text-gray-400 text-4xl mb-4">👥</div>
            <h3 className="text-lg font-medium  mb-2">No creators yet</h3>
            <p className=" mb-4">Add creators to start scraping content for inspiration</p>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add Creator
            </button>
          </div>
        )}
      </div>

      {/* Add Creator Modal */}
      {isAddModalOpen && (
        <AddCreatorModal
          onClose={() => setIsAddModalOpen(false)}
          onSubmit={handleAddCreator}
          isLoading={addCreatorMutation.isLoading}
        />
      )}

      {/* Edit Creator Modal */}
      {editingCreator && (
        <EditCreatorModal
          creator={editingCreator}
          onClose={() => setEditingCreator(null)}
          onSubmit={(updates) => handleUpdateCreator(editingCreator.id, updates)}
          isLoading={updateCreatorMutation.isLoading}
        />
      )}
    </div>
  )
}

const AddCreatorModal: React.FC<{
  onClose: () => void
  onSubmit: (data: any) => void
  isLoading: boolean
}> = ({ onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    name: "",
    handle: "",
    platform: "linkedin",
    profileUrl: "",
    description: "",
    scrapingConfig: {
      enabled: true,
      frequency: "daily",
      maxPosts: 10,
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-medium  mb-4">Add New Creator</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Creator Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Handle/Username</label>
            <input
              type="text"
              required
              value={formData.handle}
              onChange={(e) => setFormData({ ...formData, handle: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              placeholder="johndoe"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Platform</label>
            <select
              value={formData.platform}
              onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            >
              <option value="linkedin">LinkedIn</option>
              <option value="twitter">Twitter/X</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Profile URL</label>
            <input
              type="url"
              value={formData.profileUrl}
              onChange={(e) => setFormData({ ...formData, profileUrl: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              placeholder="https://linkedin.com/in/johndoe"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Brief description of the creator and why you want to scrape their content"
            />
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-medium text-primary">Scraping Configuration</h4>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="scrapingEnabled"
                checked={formData.scrapingConfig.enabled}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    scrapingConfig: { ...formData.scrapingConfig, enabled: e.target.checked },
                  })
                }
                className="h-4 w-4  focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="scrapingEnabled" className="ml-2 text-sm text-primary">
                Enable automatic scraping
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-1">Scraping Frequency</label>
              <select
                value={formData.scrapingConfig.frequency}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    scrapingConfig: { ...formData.scrapingConfig, frequency: e.target.value },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="manual">Manual Only</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-1">Max Posts per Scrape</label>
              <input
                type="number"
                min="1"
                max="50"
                value={formData.scrapingConfig.maxPosts}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    scrapingConfig: { ...formData.scrapingConfig, maxPosts: parseInt(e.target.value) },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-primary bg-foregroundx rounded-md  transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isLoading ? "Adding..." : "Add Creator"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const EditCreatorModal: React.FC<{
  creator: any
  onClose: () => void
  onSubmit: (data: any) => void
  isLoading: boolean
}> = ({ creator, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    scrapingConfig: creator.scrapingConfig || {
      enabled: true,
      frequency: "daily",
      maxPosts: 10,
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-medium  mb-4">Configure Creator: {creator.name}</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-primary">Scraping Configuration</h4>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="scrapingEnabled"
                checked={formData.scrapingConfig.enabled}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    scrapingConfig: { ...formData.scrapingConfig, enabled: e.target.checked },
                  })
                }
                className="h-4 w-4  focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="scrapingEnabled" className="ml-2 text-sm text-primary">
                Enable automatic scraping
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-1">Scraping Frequency</label>
              <select
                value={formData.scrapingConfig.frequency}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    scrapingConfig: { ...formData.scrapingConfig, frequency: e.target.value },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="manual">Manual Only</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-1">Max Posts per Scrape</label>
              <input
                type="number"
                min="1"
                max="50"
                value={formData.scrapingConfig.maxPosts}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    scrapingConfig: { ...formData.scrapingConfig, maxPosts: parseInt(e.target.value) },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-primary bg-foregroundx rounded-md  transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isLoading ? "Updating..." : "Update Config"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreatorManagement
